import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Car, Truck, ChevronRight, Calculator, Check, MessageSquare, Phone } from 'lucide-react';
import { ContactDropdown } from './ContactDropdown';

const VEHICLE_SIZES = [
  { id: 'sedan', name: 'Sedan / Coupe', icon: '🚗', examples: 'Civic, Camry, BMW 3-Series' },
  { id: 'suv', name: 'SUV / Crossover', icon: '🚙', examples: 'RAV4, CR-V, X5' },
  { id: 'truck', name: 'Truck / Large SUV', icon: '🛻', examples: 'F-150, Tahoe, Tundra' },
  { id: 'van', name: 'Van / XL Vehicle', icon: '🚐', examples: 'Sprinter, Suburban, RV' },
];

const PAINT_CONDITIONS = [
  { id: 'good', name: 'Good', description: 'Minor swirls, well maintained' },
  { id: 'moderate', name: 'Moderate', description: 'Visible swirls and light scratches' },
  { id: 'poor', name: 'Poor', description: 'Heavy swirls, scratches, oxidation' },
];

const PRICING = {
  sedan: {
    oneStep: 550,
    twoStep: 850,
    ceramicAddon: 250,
    oneStepCeramic: 950,
    twoStepCeramic: 1100,
    oneStep9yr: 1400,
    twoStep9yr: 1750,
  },
  suv: {
    oneStep: 650,
    twoStep: 1000,
    ceramicAddon: 300,
    oneStepCeramic: 1100,
    twoStepCeramic: 1300,
    oneStep9yr: 1600,
    twoStep9yr: 1950,
  },
  truck: {
    oneStep: 750,
    twoStep: 1150,
    ceramicAddon: 350,
    oneStepCeramic: 1250,
    twoStepCeramic: 1500,
    oneStep9yr: 1850,
    twoStep9yr: 2200,
  },
  van: {
    oneStep: 850,
    twoStep: 1300,
    ceramicAddon: 400,
    oneStepCeramic: 1400,
    twoStepCeramic: 1700,
    oneStep9yr: 2050,
    twoStep9yr: 2400,
  },
};

export const PriceEstimator = ({ isOpen, onClose, defaultService = 'correction' }) => {
  const [step, setStep] = useState(1);
  const [vehicleSize, setVehicleSize] = useState('');
  const [paintCondition, setPaintCondition] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState({ year: '', make: '', model: '', color: '' });

  const resetForm = () => {
    setStep(1);
    setVehicleSize('');
    setPaintCondition('');
    setSelectedService('');
    setVehicleInfo({ year: '', make: '', model: '', color: '' });
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  const isStep1Valid = vehicleInfo.year.trim().length >= 2 && vehicleInfo.make.trim().length >= 2 && vehicleInfo.model.trim().length >= 2;

  const getServices = () => {
    if (!vehicleSize) return [];
    const prices = PRICING[vehicleSize];
    
    // Ceramic Coating page: Only ceramic packages (no standalone paint corrections)
    if (defaultService === 'ceramic') {
      return [
        {
          id: 'oneStepCeramic',
          name: '1-Step + 3-5 Year Coating',
          description: 'Light correction + 3-5 year ceramic coating protection',
          price: prices.oneStepCeramic,
          includes: ['Light swirl removal', 'Single-stage polish', '3-5 year ceramic coating', 'Hydrophobic protection', 'UV protection'],
        },
        {
          id: 'twoStepCeramic',
          name: '2-Step + 3-5 Year Coating',
          description: 'Full correction + 3-5 year ceramic coating for showroom finish',
          price: prices.twoStepCeramic,
          includes: ['Heavy swirl removal', 'Scratch correction', 'Two-stage polish', '3-5 year ceramic coating', 'Hydrophobic protection', 'UV protection'],
          popular: true,
        },
        {
          id: 'oneStep9yr',
          name: '1-Step + 9 Year "Lifetime" Coating',
          description: 'Light correction + ultra-premium 9 year lifetime ceramic protection',
          price: prices.oneStep9yr,
          includes: ['Light swirl removal', 'Single-stage polish', '9 year lifetime ceramic coating', 'Hydrophobic protection', 'UV protection', 'Premium warranty'],
        },
        {
          id: 'twoStep9yr',
          name: '2-Step + 9 Year "Lifetime" Coating',
          description: 'Full correction + ultra-premium 9 year lifetime ceramic for the ultimate finish',
          price: prices.twoStep9yr,
          includes: ['Heavy swirl removal', 'Scratch correction', 'Two-stage polish', '9 year lifetime ceramic coating', 'Hydrophobic protection', 'UV protection', 'Premium warranty'],
          premium: true,
        },
      ];
    }

    // Paint Correction page: Standalone corrections + ceramic add-on options
    return [
      {
        id: 'oneStep',
        name: '1-Step Paint Correction',
        description: 'Light correction to remove minor swirls + protective wax finish',
        price: prices.oneStep,
        includes: ['Light swirl removal', 'Single-stage polish', 'Protective wax sealant', '3-month protection'],
      },
      {
        id: 'twoStep',
        name: '2-Step Paint Correction',
        description: 'Full correction for moderate to heavy defects + protective wax',
        price: prices.twoStep,
        includes: ['Heavy swirl removal', 'Scratch correction', 'Two-stage polish', 'Protective wax sealant', '3-month protection'],
        recommended: paintCondition === 'poor',
      },
      {
        id: 'oneStepCeramic',
        name: '1-Step + 3-5 Year Ceramic Coating',
        description: 'Light correction + 3-5 year ceramic coating protection',
        price: prices.oneStepCeramic,
        includes: ['Light swirl removal', 'Single-stage polish', '3-5 year ceramic coating', 'Hydrophobic protection', 'UV protection'],
        popular: true,
      },
    ];
  };

  const getEstimate = () => {
    if (!vehicleSize || !selectedService) return null;
    const services = getServices();
    return services.find(s => s.id === selectedService);
  };

  const buildEstimateSms = () => {
    const estimate = getEstimate();
    const parts = [];
    if (vehicleInfo.year || vehicleInfo.make || vehicleInfo.model) {
      parts.push(`Vehicle: ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}${vehicleInfo.color ? ` (${vehicleInfo.color})` : ''}.`);
    }
    if (estimate) {
      parts.push(`Interested in: ${estimate.name} (Est. $${estimate.price}).`);
    }
    parts.push("Can I book an appointment?");
    const body = encodeURIComponent(parts.join(' '));
    return `sms:5598801818?&body=${body}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/85"
            onClick={handleClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-2xl max-h-[90vh] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black">
              <div className="flex items-center gap-3">
                <Calculator className="w-6 h-6 text-cyan-400" />
                <h3 className="font-heading text-xl font-semibold uppercase tracking-wider text-white">
                  Price Estimator
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 p-4 bg-neutral-900/50">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s ? 'bg-cyan-500 text-black' : 'bg-white/10 text-gray-500'
                  }`}>
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 ${step > s ? 'bg-cyan-500' : 'bg-white/10'}`} />}
                </div>
              ))}
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {/* Step 1: Vehicle Info */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-6"
                >
                  <div>
                    <h4 className="text-lg font-semibold mb-4">Tell us about your vehicle</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Year</label>
                        <input
                          type="text"
                          placeholder="2024"
                          value={vehicleInfo.year}
                          onChange={(e) => setVehicleInfo({...vehicleInfo, year: e.target.value})}
                          className="w-full bg-neutral-800 border border-white/10 rounded px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Make</label>
                        <input
                          type="text"
                          placeholder="Toyota"
                          value={vehicleInfo.make}
                          onChange={(e) => setVehicleInfo({...vehicleInfo, make: e.target.value})}
                          className="w-full bg-neutral-800 border border-white/10 rounded px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Model</label>
                        <input
                          type="text"
                          placeholder="Camry"
                          value={vehicleInfo.model}
                          onChange={(e) => setVehicleInfo({...vehicleInfo, model: e.target.value})}
                          className="w-full bg-neutral-800 border border-white/10 rounded px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Color</label>
                        <input
                          type="text"
                          placeholder="Black"
                          value={vehicleInfo.color}
                          onChange={(e) => setVehicleInfo({...vehicleInfo, color: e.target.value})}
                          className="w-full bg-neutral-800 border border-white/10 rounded px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => isStep1Valid && setStep(2)}
                    disabled={!isStep1Valid}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue <ChevronRight className="w-5 h-5" />
                  </button>
                  {!isStep1Valid && (
                    <p className="text-xs text-gray-500 text-center mt-2">Please fill in Year, Make, and Model (at least 2 characters each)</p>
                  )}
                </motion.div>
              )}

              {/* Step 2: Vehicle Size */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-6"
                >
                  <div>
                    <h4 className="text-lg font-semibold mb-4">What size is your vehicle?</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {VEHICLE_SIZES.map((size) => (
                        <button
                          key={size.id}
                          onClick={() => setVehicleSize(size.id)}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            vehicleSize === size.id
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl">{size.icon}</span>
                            <span className="font-semibold">{size.name}</span>
                          </div>
                          <p className="text-sm text-gray-400">{size.examples}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => vehicleSize && setStep(3)}
                      disabled={!vehicleSize}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      Continue <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Paint Condition */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-6"
                >
                  <div>
                    <h4 className="text-lg font-semibold mb-4">How would you describe your paint condition?</h4>
                    <div className="space-y-3">
                      {PAINT_CONDITIONS.map((condition) => (
                        <button
                          key={condition.id}
                          onClick={() => setPaintCondition(condition.id)}
                          className={`w-full p-4 rounded-lg border text-left transition-all ${
                            paintCondition === condition.id
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="font-semibold mb-1">{condition.name}</div>
                          <p className="text-sm text-gray-400">{condition.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => setStep(2)}
                      className="flex-1 btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => paintCondition && setStep(4)}
                      disabled={!paintCondition}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      See Pricing <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Service Selection & Estimate */}
              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-6"
                >
                  <div>
                    <h4 className="text-lg font-semibold mb-4">Select your service</h4>
                    <div className="space-y-4">
                      {getServices().map((service) => (
                        <button
                          key={service.id}
                          onClick={() => setSelectedService(service.id)}
                          className={`w-full p-4 rounded-lg border text-left transition-all relative ${
                            selectedService === service.id
                              ? 'border-cyan-500 bg-cyan-500/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          {service.popular && (
                            <span className="absolute -top-2 right-4 bg-cyan-500 text-black text-xs font-bold px-2 py-0.5 rounded">
                              POPULAR
                            </span>
                          )}
                          {service.recommended && (
                            <span className="absolute -top-2 right-4 bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded">
                              RECOMMENDED
                            </span>
                          )}
                          {service.premium && (
                            <span className="absolute -top-2 right-4 bg-purple-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                              PREMIUM
                            </span>
                          )}
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-semibold text-white">{service.name}</div>
                            <div className="text-2xl font-bold text-cyan-400">${service.price}</div>
                          </div>
                          <p className="text-sm text-gray-400 mb-3">{service.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {service.includes.map((item, i) => (
                              <span key={i} className="text-xs bg-white/5 text-gray-300 px-2 py-1 rounded">
                                ✓ {item}
                              </span>
                            ))}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Estimate Summary */}
                  {selectedService && getEstimate() && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                      <h5 className="font-semibold text-cyan-400 mb-2">Your Estimate</h5>
                      <div className="text-sm text-gray-300 mb-3">
                        {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model} {vehicleInfo.color && `(${vehicleInfo.color})`}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">{getEstimate().name}</span>
                        <span className="text-3xl font-bold text-white">${getEstimate().price}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        *Final price may vary based on vehicle condition. Contact us for exact quote.
                      </p>
                    </div>
                  )}

                  {/* Send Info CTA */}
                  {selectedService && getEstimate() && (
                    <div className="bg-neutral-900 border border-white/10 rounded-lg p-4 text-center">
                      <p className="text-gray-300 mb-3">
                        Like the price? Want more info? Send us your details via text!
                      </p>
                      <a
                        href={buildEstimateSms()}
                        className="w-full btn-primary flex items-center justify-center gap-2"
                        data-testid="estimator-send-info-btn"
                      >
                        <MessageSquare className="w-5 h-5" /> Send Info via Text
                      </a>
                      <p className="text-xs text-gray-500 mt-2">Opens your messaging app with your info pre-filled</p>
                    </div>
                  )}
                  
                  <div className="flex gap-4">
                    <button
                      onClick={() => setStep(3)}
                      className="flex-1 btn-secondary"
                    >
                      Back
                    </button>
                    <div className="flex-1">
                      <ContactDropdown 
                        label="Contact Us" 
                        className="w-full btn-primary"
                        dropUp={true}
                        testId="estimator-contact-dropdown"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
